import {
  Component,
  DestroyRef,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import {
  showConsolidated as showConsolidatedCells,
  hasPreviousYear as hasPreviousYearCells,
} from '../../../services/mbrs/validators/dimension-visibility.util';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import {
  CROSS_SHEET_VALIDATION_DEBOUNCE_MS,
  FORM_CALCULATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { headerLevelClass } from '../../../utils/section-visibility.util';
import { SubmitForValidationComponent } from '../../common/submit-for-validation/submit-for-validation.component';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { SkeletonModule } from 'primeng/skeleton';
import { pickRole13SchemaFile } from '../../../services/mbrs/validators/role13-analysis-of-profit-or-loss.rules';
import { MappingStatusService } from '../../../services/mbrs/mapping-status.service';
import { EqualityValidationService, EqualityValidationError } from '../../../services/mbrs/equality-validation.service';
import { ValidationContextService } from '../../../services/mbrs/validation-context.service';
import {
  recomputeReferencedSheets,
  referencedSheetKeysFromSchema,
} from '../../../services/mbrs/cross-sheet-recompute.util';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { FieldLinkLabelComponent } from '../../common/field-link-label/field-link-label.component';
import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import {
  TOAST_DURATION_DEFAULT_MS,
  } from '../../../utils/toast-durations.util';
import { computedControlPredicate, revealValidationErrors, enableDualColumnRowAndRevealVisible } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';

interface FieldControl {
  qname: string;
  label: string;
  type: string;
  required?: boolean;
  ui: {
    controlType: string;
    currency?: string;
    placeholder?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    readonly?: boolean;
    level?: number | string;
    note?: string;
    linkTo?: {
      role: string;
      qname: string;
    };
  };
}

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: FieldControl[];
}

@Component({
  selector: 'app-role13-310100-analysis-of-profit-or-loss',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    FieldLinkLabelComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    SubmitForValidationComponent,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    FieldLinkTargetDirective,
    SkeletonModule,
  ],
  providers: [MessageService],
  templateUrl: './role13-310100-analysis-of-profit-or-loss.component.html',
  styleUrls: ['./role13-310100-analysis-of-profit-or-loss.component.css'],
})
export class Role13310100AnalysisOfProfitOrLossComponent
  implements OnInit, OnDestroy
{
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private envService = inject(EnvironmentService);
  private mappingStatus = inject(MappingStatusService);
  private equalityValidation = inject(EqualityValidationService);
  private validationContext = inject(ValidationContextService);
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  analysisOfProfitOrLossForm!: FormGroup;
  schema: FormSchema | null = null;
  isLoading = true;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Analysis of Profit or Loss (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.analysisOfProfitOrLoss
    );
  }

  /**
   * The four (dimension × period) visibility cells for this sheet, derived by
   * the shared, config-driven resolver via CrossReferenceService.getVisibility().
   * The SAME resolver + central registry feeds the pure
   * role13AnalysisOfProfitOrLossValidator, so the mounted editor and the
   * headless listing cannot drift on which columns are shown/validated.
   */
  get dimensionVisibility() {
    return this.crossRefService.getVisibility(SHEET_KEYS.analysisOfProfitOrLoss);
  }

  /**
   * Show the Consolidated columns. Previously set imperatively in
   * checkConsolidatedStatus() from filingInfo's NatureOfFinancialStatements;
   * now a live getter off the shared resolver (prereq-first, then filingInfo).
   * The util fn is imported aliased (`showConsolidatedCells`) to avoid a name
   * clash with this getter.
   *
   * **Live, not a field, on purpose.** The imperative version snapshotted this
   * in `ngOnInit` — which on a cold load (duplicated tab / hard refresh) runs
   * while `fetchAllMbrsData()` is still in flight and the filing slices are
   * empty. `'' === 'Consolidated'` is `false`, so the whole Consolidated group
   * was dropped and never restored. It also disagreed with the shared resolver,
   * which defaults an unknown Nature to SHOWN. See the 2026-08-21 entry in
   * `dimension-visibility-seam-parity.spec.ts`.
   */
  get showConsolidatedColumns(): boolean {
    return showConsolidatedCells(this.dimensionVisibility);
  }

  /**
   * Show the PreviousYear columns. Previously set imperatively in
   * checkConsolidatedStatus() from filingInfo's preparation type; now a live
   * getter off the shared resolver. The util fn is imported aliased
   * (`hasPreviousYearCells`) to avoid a name clash with this getter.
   *
   * Same cold-load reasoning as `showConsolidatedColumns`: the imperative
   * version's unknown-value branch defaulted to `true`, so a first-time-
   * preparation filing rendered a phantom `(Previous)` column that could never
   * hold a value (its header date was blank too, because there is no previous
   * financial year to format).
   */
  get hasPreviousYear(): boolean {
    return hasPreviousYearCells(this.dimensionVisibility);
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  private editingRows = new Map<string, { [key: string]: number | null }>(); // Track rows in edit mode with original values

  /** Row Mapping-Status frozen at edit-enter; while a row is mid-edit its badge
   *  shows this committed value (not the live in-edit recompute) so the pencil
   *  alone never flips it to 'Edited'. Cleared on Tick/Cancel → badge recomputes. */
  private frozenRowStatus = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();
  expandedSections = new Set<string>(); // Track expanded sections

  private validationErrors = new Map<string, string[]>();

  // Cross-sheet equality verdict (MPERS only): the inventory write-down line
  // (ifrs-smes:ReversalOfInventoryWritedown) must tie to the same field on
  // Role 18 SOCF — the reciprocal of the equality Role 18 already enforces
  // against this sheet. equalityValidationErrors drives hasCrossSheetValidationError
  // (red border) and its messages are merged into validationErrors (Error
  // Description + 'Failed' status via _computeRowErrorFlags). crossSheetErrorQnames
  // lets validateSameSheet preserve these frozen entries during live edits;
  // validateCrossSheet (Tick + cross-sheet listener) rebuilds them. Mirrors role 18.
  private equalityValidationErrors = new Map<string, EqualityValidationError[]>();
  private crossSheetErrorQnames = new Set<string>();
  private isLoadingState = false;
  private isCalculating = false;
  private calculatedTotalKeys: string[] = [];
  private isCancellingChanges = false; // Flag to skip validation during cancel flow
  private isStateSelfUpdate = false; // Guards against setupStateListener re-validating on self-triggered state updates
  /**
   * Set once this sheet's initial load has settled. Past that point
   * `setupStateListener` stops re-patching the form — see its docblock for why
   * a post-init repaint silently reverts a row the user has open. Mirrors
   * role23/role24, which carry the identical listener and the identical guard.
   */
  private isInitialLoadComplete = false;
  isValidationComplete = false; // Prevents "Validated -> Failed" flash on page load

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get formatted date range for current year
   * e.g., "01/01/2024 - 31/12/2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getFormattedFinancialYear('current');
  }

  /**
   * Get formatted date range for previous year
   * e.g., "01/01/2023 - 31/12/2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getFormattedFinancialYear('previous');
  }

  ngOnInit(): void {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '13',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    this.loadSchema();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // `checkConsolidatedStatus()` used to live here. It is gone, not moved.
  //
  // Its column-visibility half (`showConsolidatedColumns` / `hasPreviousYear`)
  // is now live getters off the shared dimension-visibility resolver — see
  // their docblocks for why a snapshot taken in `ngOnInit` was wrong.
  //
  // Its other half assigned `isConsolidated` from Role-2's scopeOfFiling
  // toggle, and that field was never read by this component or its template —
  // write-only since it was introduced. Both the method and the field were
  // removed rather than left as drift bait for the next reader to "keep in
  // sync". If this sheet ever needs Role-2's toggle, read it at the point of
  // use; do not reintroduce a mount-time snapshot.

  /**
   * Load form schema from JSON
   * Dynamically loads MFRS or MPERS schema based on accounting standard from Role-0
   */
  private async loadSchema(): Promise<void> {
    this.isLoading = true;

    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role13');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Determine which schema to load based on accounting standard.
    // Selection literal is shared with role13AnalysisOfProfitOrLossValidator
    // via pickRole13SchemaFile so both paths cannot drift apart — see
    // role13-analysis-of-profit-or-loss.rules.ts. Reads the same accounting
    // standard CrossReferenceService.isMFRS() resolves from, preserving the
    // "MFRS iff exact literal, else MPERS" semantics.
    const filingData = this.stateService.getActiveFilingInfoData();
    const accountingStandard =
      filingData?.[
        'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'
      ] || '';
    const schemaFile = pickRole13SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    fetchSheetSchema<FormSchema>(
        this.loadTraceHandle,
        `assets/forms/${schemaFile}`,
      )
      .then((data: FormSchema) => {
        this.schema = data;
        this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
        this.buildForm();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
        this.initializeExpandedSections();
        this.setupStateListener(); // Listen for external state updates (e.g., from backend API)
        this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
        this.setupEditPipeline();

        // Reveal the sheet's already-computed errors now that the form exists
        // and has been populated. Deterministic replacement for the filing-form
        // container's setTimeout-driven markAllAsTouched, which silently no-ops
        // when a sheet's async init has not yet assigned its FormGroup — see
        // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
        revealValidationErrors(this.analysisOfProfitOrLossForm, {
          isComputed: computedControlPredicate(this.schema?.controls),
        });
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      })
      .catch((error) => {
        // Classified rather than reported as a bare schema failure: this one
        // `.catch` also covers the cross-sheet preload, buildForm, the edit
        // pipeline and loadSavedState, so "Failed to load form schema" named
        // the wrong step for most of what can land here.
        this.loadTraceHandle.fail(classifySheetInitError(error), error);
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      });
  }

  /**
   * Build reactive form from schema
   * Always creates form controls for all possible columns (4 max)
   * HTML will conditionally show/hide based on showConsolidatedColumns and hasPreviousYear
   */
  private buildForm(): void {
    if (!this.schema) return;

    const formControls: any = {};

    this.schema.controls.forEach((control) => {
      // Skip headers
      if (control.ui.controlType === 'section-header') return;

      const validators: ValidatorFn[] = [];

      if (control.ui.dualColumn) {
        // Always create all 4 controls - HTML will show/hide based on flags
        // This matches Role-12 pattern
        // Initialize with null instead of 0 so empty fields show as blank
        formControls[
          this.getXbrlControlName(control.qname, 'Consolidated', 'Current')
        ] = new FormControl(null, validators);
        formControls[
          this.getXbrlControlName(control.qname, 'Consolidated', 'Previous')
        ] = new FormControl(null, validators);
        formControls[
          this.getXbrlControlName(control.qname, 'Separate', 'Current')
        ] = new FormControl(null, validators);
        formControls[
          this.getXbrlControlName(control.qname, 'Separate', 'Previous')
        ] = new FormControl(null, validators);
      }
    });

    this.analysisOfProfitOrLossForm = this.fb.group(formControls);

    // Disable all fields by default (users must click pencil to edit)
    this.disableAllFields();
  }

  /**
   * Disable all fields by default (read-only mode)
   * Users must click pencil icon to enable editing
   * Called after form creation to prevent "changed after checked" warnings
   */
  private disableAllFields(): void {
    if (!this.schema) return;

    this.schema.controls.forEach((control) => {
      // Disable all monetary fields (both calculated and editable)
      if (control.ui.controlType === 'number' && control.ui.dualColumn) {
        // Disable all 4 column variants
        this.analysisOfProfitOrLossForm
          .get(
            this.getXbrlControlName(control.qname, 'Consolidated', 'Current')
          )
          ?.disable({ emitEvent: false });
        this.analysisOfProfitOrLossForm
          .get(
            this.getXbrlControlName(control.qname, 'Consolidated', 'Previous')
          )
          ?.disable({ emitEvent: false });
        this.analysisOfProfitOrLossForm
          .get(this.getXbrlControlName(control.qname, 'Separate', 'Current'))
          ?.disable({ emitEvent: false });
        this.analysisOfProfitOrLossForm
          .get(this.getXbrlControlName(control.qname, 'Separate', 'Previous'))
          ?.disable({ emitEvent: false });
      }
    });
  }

  /**
   * Wire the live edit pipeline: calc → validateSameSheet → validateCrossSheet → evaluateStatus.
   * No persist callback — commits are deferred to saveEditMode (Tick).
   * Guard: skip while cancelling changes.
   */
  private setupEditPipeline(): void {
    buildEditModeLivePipeline({
      source$: this.analysisOfProfitOrLossForm.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isCancellingChanges,
      calculate: () => this.calculateTotals(),
      validateSameSheet: () => { if (!this.isLoadingState) this.validateSameSheet(); },
      validateCrossSheet: () => this.validateCrossSheet(),
      evaluateStatus: () => this.evaluateSheetStatus(),
    });
  }

  /**
   * Check if control is a section header
   */
  isSectionHeader(control: FieldControl): boolean {
    return control.ui.controlType === 'section-header';
  }

  /**
   * PHASE 2: Get XBRL control name using simplified bracket notation
   * Converts from old concatenated format to new bracket format
   *
   * OLD: qnameConsolidatedCurrent
   * NEW: qname[Consolidated][CurrentYear]
   *
   * @param qname The XBRL concept name
   * @param consolidatedOrSeparate 'Consolidated' or 'Separate'
   * @param period 'Current' or 'Previous'
   * @returns Control name in bracket format
   */
  getXbrlControlName(
    qname: string,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous'
  ): string {
    const periodLabel = period === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${qname}[${consolidatedOrSeparate}][${periodLabel}]`;
  }

  /**
   * Get header CSS class based on level. Delegates to the shared
   * `headerLevelClass`, which maps a numeric or `'hN'` level 1:1 to
   * `header-level-N` and falls back to `header-level-1`.
   */
  getHeaderClass(level?: number | string): string {
    return headerLevelClass(level);
  }

  /**
   * Check if field is calculated.
   *
   * NOTE: this intentionally checks ONLY `ui.calculated === true` and does NOT
   * include `ui.readonly`. The call site that sets `derived: true` on a row-status
   * entry (~line 2074) uses the WIDER set `!!(ui.calculated || ui.readonly)` to
   * match the `enterEditMode` guard's exclusion logic. Do NOT simplify that call
   * site to `isCalculatedField(control)` — it would silently drop readonly-only
   * rows from the derived gate and let them hit the Edited branch incorrectly.
   */
  /**
   * Does this row's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory`, so this sheet answers the
   * question the same way as the other 16.
   *
   * **This sheet is why "no mandatory fields today" is not a reason to skip
   * wiring the marker.** When the marker seam rolled out (2026-08-11) this
   * sheet declared zero mandatory controls, so it was classified "nothing to
   * do" and left unwired. Hours later the PO's enumerated totals list flagged
   * its calculated totals — and the flags landed with nowhere to render, so
   * the `*` silently did not appear until smoke testing caught it. Wire the
   * marker whenever a sheet renders schema-driven labels, whatever its current
   * mandatory count. The PO's enumerated totals are recorded per field in the sheet's spec under `docs/specifications/sheets/`.
   *
   * `crossSheetValue` is supplied so a cross-sheet `requiredIf` resolves;
   * same-sheet `selfData` is deliberately NOT passed, because this sheet keys
   * its values per cell (`qname[Dimension][Period]`) rather than by bare
   * qname, so a same-sheet lookup would silently miss. Add it — with the right
   * key shape — if a same-sheet conditional rule is ever declared here.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  isCalculatedField(control: FieldControl): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Initialize all collapsible sections as expanded (Level 2, 3, 4, 5)
   */
  private initializeExpandedSections(): void {
    if (!this.schema) return;
    this.schema.controls.forEach((control: FieldControl) => {
      if (
        control.ui.level === 2 ||
        control.ui.level === 'h2' ||
        control.ui.level === 3 ||
        control.ui.level === 'h3' ||
        control.ui.level === 4 ||
        control.ui.level === 'h4' ||
        control.ui.level === 5 ||
        control.ui.level === 'h5'
      ) {
        this.expandedSections.add(control.qname);
      }
    });
  }

  /**
   * Toggle section expand/collapse
   */
  toggleSection(qname: string): void {
    if (this.expandedSections.has(qname)) {
      this.expandedSections.delete(qname);
    } else {
      this.expandedSections.add(qname);
    }
  }

  /**
   * Check if section is expanded
   */
  isSectionExpanded(qname: string): boolean {
    return this.expandedSections.has(qname);
  }

  /**
   * Determine if a row should be shown based on section expand/collapse state
   */
  shouldShowRow(control: FieldControl, index: number): boolean {
    if (!this.schema) return true;

    // Always show level 1 headers
    if (control.ui.level === 1 || control.ui.level === 'h1') {
      return true;
    }

    // Always show level 2 headers
    if (control.ui.level === 2 || control.ui.level === 'h2') {
      return true;
    }

    // Show level 3 headers if parent level 2 is expanded
    if (control.ui.level === 3 || control.ui.level === 'h3') {
      let parentLevel2Qname = '';
      for (let controlIndex = index - 1; controlIndex >= 0; controlIndex--) {
        const prevControl = this.schema.controls[controlIndex];
        if (prevControl.ui.level === 2 || prevControl.ui.level === 'h2') {
          parentLevel2Qname = prevControl.qname;
          break;
        }
      }
      if (parentLevel2Qname) {
        return this.isSectionExpanded(parentLevel2Qname);
      }
      return true;
    }

    // Show level 4 headers if parent level 2 AND level 3 are expanded
    if (control.ui.level === 4 || control.ui.level === 'h4') {
      let parentLevel2Qname = '';
      let parentLevel3Qname = '';
      for (let controlIndex = index - 1; controlIndex >= 0; controlIndex--) {
        const prevControl = this.schema.controls[controlIndex];
        if (prevControl.ui.level === 2 || prevControl.ui.level === 'h2') {
          parentLevel2Qname = prevControl.qname;
          break;
        }
        if (
          (prevControl.ui.level === 3 || prevControl.ui.level === 'h3') &&
          !parentLevel3Qname
        ) {
          parentLevel3Qname = prevControl.qname;
        }
      }
      if (parentLevel2Qname && parentLevel3Qname) {
        return (
          this.isSectionExpanded(parentLevel2Qname) &&
          this.isSectionExpanded(parentLevel3Qname)
        );
      }
      if (parentLevel2Qname) {
        return this.isSectionExpanded(parentLevel2Qname);
      }
      return true;
    }

    // Show level 5 headers if parent level 2, 3, AND 4 are expanded
    if (control.ui.level === 5 || control.ui.level === 'h5') {
      let parentLevel2Qname = '';
      let parentLevel3Qname = '';
      let parentLevel4Qname = '';
      for (let controlIndex = index - 1; controlIndex >= 0; controlIndex--) {
        const prevControl = this.schema.controls[controlIndex];
        if (prevControl.ui.level === 2 || prevControl.ui.level === 'h2') {
          parentLevel2Qname = prevControl.qname;
          break;
        }
        if (
          (prevControl.ui.level === 3 || prevControl.ui.level === 'h3') &&
          !parentLevel3Qname
        ) {
          parentLevel3Qname = prevControl.qname;
        }
        if (
          (prevControl.ui.level === 4 || prevControl.ui.level === 'h4') &&
          !parentLevel4Qname
        ) {
          parentLevel4Qname = prevControl.qname;
        }
      }
      if (parentLevel2Qname && parentLevel3Qname && parentLevel4Qname) {
        return (
          this.isSectionExpanded(parentLevel2Qname) &&
          this.isSectionExpanded(parentLevel3Qname) &&
          this.isSectionExpanded(parentLevel4Qname)
        );
      }
      if (parentLevel2Qname && parentLevel3Qname) {
        return (
          this.isSectionExpanded(parentLevel2Qname) &&
          this.isSectionExpanded(parentLevel3Qname)
        );
      }
      if (parentLevel2Qname) {
        return this.isSectionExpanded(parentLevel2Qname);
      }
      return true;
    }

    // For regular fields (non-headers), we need to determine the parent by looking at the
    // hierarchical structure. The parent is the section that "owns" this field.
    //
    // Example structure:
    // Level 3: Revenue
    //   Level 4: Sale of goods
    //     Field: Telecom revenue
    //     Field: Property revenue
    //   Level 4: Rendering services
    //     Field: Entertainment revenue
    //   Field: Dividend income  <-- Parent is Level 3 Revenue, NOT Level 4 Rendering services
    //
    // Algorithm: Find the first header going backward, but if we encounter a header at the
    // same or higher level as that header before hitting Level 2, then the higher-level header
    // is the true parent.

    // Step 1: Find the closest header
    let closestHeaderQname = '';
    let closestHeaderLevel = 0;
    let closestHeaderIndex = -1;

    for (let controlIndex = index - 1; controlIndex >= 0; controlIndex--) {
      const prevControl = this.schema.controls[controlIndex];
      if (this.isSectionHeader(prevControl)) {
        closestHeaderQname = prevControl.qname;
        closestHeaderLevel =
          typeof prevControl.ui.level === 'string'
            ? parseInt(prevControl.ui.level.replace('h', ''))
            : prevControl.ui.level || 0;
        closestHeaderIndex = controlIndex;
        break;
      }
    }

    if (!closestHeaderQname) {
      return true; // No parent found, show the field
    }

    // Step 2: Determine the true parent by checking section boundaries
    // A field belongs to a header if there are NO headers of the same or higher level between them
    // Example:
    //   Level 4: Header A
    //     Field 1
    //     Field 2
    //   Level 4: Header B    <- This marks the end of Header A's section
    //     Field 3
    //   Field 4              <- After all Level 4s, belongs to Level 3

    let trueParentQname = closestHeaderQname;
    let trueParentLevel = closestHeaderLevel;

    // Look for section boundaries between the closest header and this field
    // A section ends when we find:
    // 1. Another header at the same or higher level (same or lower number), OR
    // 2. A calculated/total field (which marks the end of a subsection)
    let foundSectionBoundary = false;

    for (let controlIndex = closestHeaderIndex + 1; controlIndex < index; controlIndex++) {
      const ctrl = this.schema.controls[controlIndex];
      if (this.isSectionHeader(ctrl)) {
        const level =
          typeof ctrl.ui.level === 'string'
            ? parseInt(ctrl.ui.level.replace('h', ''))
            : ctrl.ui.level || 0;

        // If we find a header at the same or higher level, the section ended
        if (level <= closestHeaderLevel) {
          foundSectionBoundary = true;
          break;
        }
      } else if (ctrl.ui?.calculated === true) {
        // Found a calculated/total field - this marks the end of the subsection
        // Fields after this belong to the parent level
        foundSectionBoundary = true;
        break;
      }
    }

    // If there's a section boundary, look for the true parent at a higher level
    if (foundSectionBoundary) {
      for (let controlIndex = closestHeaderIndex - 1; controlIndex >= 0; controlIndex--) {
        const prevControl = this.schema.controls[controlIndex];
        if (this.isSectionHeader(prevControl)) {
          const level =
            typeof prevControl.ui.level === 'string'
              ? parseInt(prevControl.ui.level.replace('h', ''))
              : prevControl.ui.level || 0;

          if (level < closestHeaderLevel) {
            // Found a higher-level header - this is the true parent
            trueParentQname = prevControl.qname;
            trueParentLevel = level;
            break; // Stop at the first higher-level header found
          }

          if (level === 2) {
            break; // Stop at Level 2
          }
        }
      }
    }

    // Step 3: Check if the true parent is expanded
    if (!this.isSectionExpanded(trueParentQname)) {
      return false;
    }

    // Step 4: Check all ancestor levels above the true parent
    let parentLevel2Qname = '';
    let parentLevel3Qname = '';
    let parentLevel4Qname = '';

    for (let controlIndex = index - 1; controlIndex >= 0; controlIndex--) {
      const prevControl = this.schema.controls[controlIndex];
      if (this.isSectionHeader(prevControl)) {
        const level =
          typeof prevControl.ui.level === 'string'
            ? parseInt(prevControl.ui.level.replace('h', ''))
            : prevControl.ui.level || 0;

        if (level === 2) {
          parentLevel2Qname = prevControl.qname;
          break;
        }
        if (level === 3 && !parentLevel3Qname) {
          parentLevel3Qname = prevControl.qname;
        }
        if (level === 4 && !parentLevel4Qname) {
          parentLevel4Qname = prevControl.qname;
        }
      }
    }

    // Check ancestors based on the true parent level
    if (trueParentLevel === 4) {
      // Parent is Level 4, check Level 3 and 2 above it
      if (parentLevel3Qname && !this.isSectionExpanded(parentLevel3Qname))
        return false;
      if (parentLevel2Qname && !this.isSectionExpanded(parentLevel2Qname))
        return false;
    } else if (trueParentLevel === 3) {
      // Parent is Level 3, check Level 2 above it
      if (parentLevel2Qname && !this.isSectionExpanded(parentLevel2Qname))
        return false;
    }
    // If parent is Level 2, no ancestors to check

    return true;
  }

  /**
   * Calculate all totals and derived fields
   */
  /**
   * Get the field name with correct prefix based on accounting standard
   * MFRS uses 'ifrs-full:' and 'ssmt-mfrs:' prefixes
   * MPERS uses 'ifrs-smes:' and 'ssmt-mpers:' prefixes
   */
  // private getFieldName(baseFieldName: string): string {
  //   const isMFRS = this.crossRefService.isMFRS();

  //   if (baseFieldName.startsWith('ifrs-full:')) {
  //     return isMFRS ? baseFieldName : baseFieldName.replace('ifrs-full:', 'ifrs-smes:');
  //   } else if (baseFieldName.startsWith('ssmt-mfrs:')) {
  //     return isMFRS ? baseFieldName : baseFieldName.replace('ssmt-mfrs:', 'ssmt-mpers:');
  //   }

  //   // If it doesn't have a prefix or has different prefix, return as-is
  //   return baseFieldName;
  // }

  private calculateMFRSTotals(suffix: string): void {
    // ======================== REVENUE CALCULATIONS ========================
    // // 1. Total revenue from sale of goods
    // const totalRevenueFromSaleOfGoods =
    //   this.getFieldValue(
    //     'ifrs-full:RevenueFromSaleOfTelecommunicationEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mfrs:RevenueFromSaleOfProperty', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:RevenueFromSaleOfConstructionContracts',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:RevenueFromSaleOfCleanWaterTreatmentAndDisposalOfWasteWater',
    //     suffix
    //   ) +
    //   this.getFieldValue('ifrs-full:RevenueFromSaleOfFoodAndBeverage', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:RevenueFromSaleOfAgriculturalProduce',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:RevenueFromSaleOfOilAndGasProducts',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mfrs:RevenueFromSaleOfOtherGoods', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:RevenueFromSaleOfGoods' + suffix)
    //   ?.setValue(totalRevenueFromSaleOfGoods, { emitEvent: false });
    // // 2. Total revenue from rendering of services
    // const totalRevenueFromServices =
    //   this.getFieldValue(
    //     'ssmt-mfrs:RevenueFromEntertainmentOperations',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:RevenueFromRenderingOfTelecommunicationServices',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:RevenueFromRenderingOfTransportServices',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:RevenueFromRenderingOfInformationTechnologyServices',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mfrs:RevenueFromEducationalServices', suffix) +
    //   this.getFieldValue('ssmt-mfrs:RevenueFromHealthcareServices', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:RevenueFromRenderingOfCargoAndMailTransportServices',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:RevenueFromRenderingOfOtherServices',
    //     suffix
    //   );
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:RevenueFromRenderingOfServices' + suffix)
    //   ?.setValue(totalRevenueFromServices, { emitEvent: false });
    // // 3. Total interest income
    // const totalInterestIncome =
    //   this.getFieldValue(
    //     'ifrs-full:InterestIncomeOnLoansAndReceivables',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:InterestIncomeOnOtherFinancialAssets',
    //     suffix
    //   );
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:RevenueFromInterest' + suffix)
    //   ?.setValue(totalInterestIncome, { emitEvent: false });
    // // 4. Total other fee and commission income
    // const totalFeeAndCommission =
    //   this.getFieldValue('ifrs-full:BrokerageFeeIncome', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:PortfolioAndOtherManagementFeeIncome',
    //     suffix
    //   ) +
    //   this.getFieldValue('ifrs-full:OtherFeeAndCommissionIncome', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:FeeAndCommissionIncome' + suffix)
    //   ?.setValue(totalFeeAndCommission, { emitEvent: false });
    // // 5. Total revenue
    // const totalRevenue =
    //   this.getFieldValue('ifrs-full:RevenueFromSaleOfGoods', suffix) +
    //   this.getFieldValue('ifrs-full:RevenueFromRenderingOfServices', suffix) +
    //   this.getFieldValue('ifrs-full:RevenueFromInterest', suffix) +
    //   this.getFieldValue('ifrs-full:FeeAndCommissionIncome', suffix) +
    //   this.getFieldValue('ifrs-full:RevenueFromDividends', suffix) +
    //   this.getFieldValue('ifrs-full:RentalIncome', suffix) +
    //   this.getFieldValue('ifrs-full:RevenueFromRoyalties', suffix) +
    //   this.getFieldValue('ifrs-full:OtherRevenue', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:Revenue' + suffix)
    //   ?.setValue(totalRevenue, { emitEvent: false });
    // // ======================== COST OF SALES ========================
    // // 6. Total cost of sales
    // const totalCostOfSales =
    //   this.getFieldValue('ssmt-mfrs:CostOfInventories', suffix) +
    //   this.getFieldValue('ssmt-mfrs:ConstructionContractCost', suffix) +
    //   this.getFieldValue('ifrs-full:EnergyExpense', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:PropertyDevelopmentAndProjectManagementExpense',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mfrs:OtherCostOfSales', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:CostOfSales' + suffix)
    //   ?.setValue(totalCostOfSales, { emitEvent: false });
    // // ======================== OTHER INCOME ========================
    // // 7. Total foreign exchange gain
    // const totalForeignExchangeGain =
    //   this.getFieldValue('ssmt-mfrs:RealisedGainOnForeignExchange', suffix) +
    //   this.getFieldValue('ssmt-mfrs:UnrealisedGainOnForeignExchange', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ssmt-mfrs:GainsOnForeignExchange' + suffix)
    //   ?.setValue(totalForeignExchangeGain, { emitEvent: false });
    // // 8. Total gain on disposal
    // const totalGainOnDisposal =
    //   this.getFieldValue('ssmt-mfrs:GainsFromDisposalOfSubsidiaries', suffix) +
    //   this.getFieldValue('ssmt-mfrs:GainsFromDisposalOfAssociates', suffix) +
    //   this.getFieldValue('ssmt-mfrs:GainsFromDisposalOfJointVentures', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get(
    //     'ssmt-mfrs:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates' +
    //       suffix
    //   )
    //   ?.setValue(totalGainOnDisposal, { emitEvent: false });
    // // 9. Total reversal of impairment loss
    // const totalReversalOfImpairment =
    //   this.getFieldValue(
    //     'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLossLoansAndAdvances',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLossInvestmentProperty',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:ReversalOfImpairmentLossInLandHeldForDevelopment',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLossPropertyPlantAndEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:ReversalOfImpairmentLossForOtherAssets',
    //     suffix
    //   );
    // this.analysisOfProfitOrLossForm
    //   .get(
    //     'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLoss' + suffix
    //   )
    //   ?.setValue(totalReversalOfImpairment, { emitEvent: false });
    // // 10. Total other income (38 fields total)
    // const totalOtherIncome =
    //   // First 11 fields (before any subsections)
    //   this.getFieldValue('ssmt-mfrs:AmortisationOfDeferredIncome', suffix) +
    //   this.getFieldValue('ssmt-mfrs:BadDebtsRecovered', suffix) +
    //   this.getFieldValue('ssmt-mfrs:OtherIncomeDividend', suffix) +
    //   this.getFieldValue('ssmt-mfrs:OtherIncomeRoyaltyAndFranchise', suffix) +
    //   this.getFieldValue('ssmt-mfrs:WriteBackOfFuelCost', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:IncomeFromReimbursementsUnderInsurancePolicies',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:GrantsOrIncentivesReceivedByMalaysianGovernmentOrAgencies',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:GrantsOrIncentivesReceivedByForeignGovernmentOrAgencies',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:ContributionsReceivedByLocalContributor',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:ContributionsReceivedByForeignContributor',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:ContributionsReceivedByUnknownContributor',
    //     suffix
    //   ) +
    //   // Foreign exchange gain section (calculated subtotal)
    //   this.getFieldValue('ssmt-mfrs:GainsOnForeignExchange', suffix) +
    //   // Gain on disposal section (calculated subtotal + 4 more fields)
    //   this.getFieldValue(
    //     'ssmt-mfrs:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:GainsOnDisposalOfOtherInvestments',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:GainsOnDisposalsOfPropertyPlantAndEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:GainsOnDisposalsOfNoncurrentAssets',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:OtherGainsRecognisedInProfitOrLossFairValueMeasurementAssets',
    //     suffix
    //   ) +
    //   // Reversal of impairment section (calculated subtotal)
    //   this.getFieldValue(
    //     'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLoss',
    //     suffix
    //   ) +
    //   // Remaining 11 fields after reversal section
    //   this.getFieldValue('ifrs-full:ReversalOfInventoryWritedown', suffix) +
    //   this.getFieldValue('ssmt-mfrs:OtherInterestIncome', suffix) +
    //   this.getFieldValue('ssmt-mfrs:IncomeFromManagementFees', suffix) +
    //   this.getFieldValue('ssmt-mfrs:NetDepositsRecognised', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:GainsOnChangeInFairValueOfDerivatives',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:FairValueGainOnRecycleOfForexReserveUponDisposalOfSubsdiaries',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mfrs:OtherFeesAndCommissionIncome', suffix) +
    //   this.getFieldValue('ssmt-mfrs:OtherRentalIncome', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:OtherRentalIncomeOnLandAndBuildings',
    //     suffix
    //   ) +
    //   this.getFieldValue('ifrs-full:MiscellaneousOtherOperatingIncome', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:OtherIncome' + suffix)
    //   ?.setValue(totalOtherIncome, { emitEvent: false });
    // // ======================== EXPENSES ========================
    // // 11. Total auditor's remuneration
    // const totalAuditorsRemuneration =
    //   this.getFieldValue(
    //     'ifrs-full:AuditorsRemunerationForAuditServices',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:AuditorsRemunerationForOtherServices',
    //     suffix
    //   );
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:AuditorsRemuneration' + suffix)
    //   ?.setValue(totalAuditorsRemuneration, { emitEvent: false });
    // // 12. Total loss on disposal
    // const totalLossOnDisposal =
    //   this.getFieldValue('ssmt-mfrs:LossOnDisposalOfSubsidiaries', suffix) +
    //   this.getFieldValue('ssmt-mfrs:LossOnDisposalOfAssociates', suffix) +
    //   this.getFieldValue('ssmt-mfrs:LossOnDisposalOfJointVentures', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get(
    //     'ssmt-mfrs:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures' +
    //       suffix
    //   )
    //   ?.setValue(totalLossOnDisposal, { emitEvent: false });
    // // 13. Total employee benefits expense
    // const totalEmployeeBenefits =
    //   this.getFieldValue('ifrs-full:WagesAndSalaries', suffix) +
    //   this.getFieldValue('ssmt-mfrs:BonusExpenses', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:ExpenseFromSharebasedPaymentTransactionsWithEmployees',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mfrs:EmployeeShareBasedPayment', suffix) +
    //   this.getFieldValue('ifrs-full:SocialSecurityContributions', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:PostemploymentBenefitExpenseDefinedBenefitPlans',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:PostemploymentBenefitExpenseDefinedContributionPlans',
    //     suffix
    //   ) +
    //   this.getFieldValue('ifrs-full:OtherLongtermBenefits', suffix) +
    //   this.getFieldValue('ifrs-full:OtherShorttermEmployeeBenefits', suffix) +
    //   this.getFieldValue('ifrs-full:OtherEmployeeExpense', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:EmployeeBenefitsExpense' + suffix)
    //   ?.setValue(totalEmployeeBenefits, { emitEvent: false });
    // // 14. Total director's remuneration
    // const totalDirectorsRemuneration =
    //   this.getFieldValue('ssmt-mfrs:WagesAndSalariesToDirectors', suffix) +
    //   this.getFieldValue('ssmt-mfrs:BonusToDirectors', suffix) +
    //   this.getFieldValue('ssmt-mfrs:BenefitsInKindExpenses', suffix) +
    //   this.getFieldValue('ssmt-mfrs:DirectorsFees', suffix) +
    //   this.getFieldValue('ssmt-mfrs:PerformanceIncentives', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:RetirementBenefitsDefinedBenefitPlans',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:RetirementBenefitsDefinedContributionPlans',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:KeyManagementPersonnelCompensationSharebasedPayment',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mfrs:OtherDirectorsRemuneration', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ssmt-mfrs:DirectorsRemuneration' + suffix)
    //   ?.setValue(totalDirectorsRemuneration, { emitEvent: false });
    // // 15. Total other expenses (38 fields total)
    // const totalOtherExpenses =
    //   // Auditor's remuneration section (calculated subtotal)
    //   this.getFieldValue('ifrs-full:AuditorsRemuneration', suffix) +
    //   // 3 fields after auditor's remuneration
    //   this.getFieldValue('ifrs-full:AmortisationExpense', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:DepreciationPropertyPlantAndEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mfrs:NaturalDisasterRelatedExpenses', suffix) +
    //   // Loss on disposal section (calculated subtotal + 4 additional fields)
    //   this.getFieldValue(
    //     'ssmt-mfrs:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures',
    //     suffix
    //   ) +
    //   this.getFieldValue('ifrs-full:LossesOnDisposalsOfInvestments', suffix) +
    //   this.getFieldValue(
    //     'ifrs-full:LossesOnDisposalsOfPropertyPlantAndEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-full:LossesOnDisposalsOfNoncurrentAssets',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mfrs:OtherLossesRecognisedInProfitOrLossFairValueMeasurementAssets',
    //     suffix
    //   ) +
    //   // 2 rental/royalty fields
    //   this.getFieldValue('ifrs-full:RentalExpense', suffix) +
    //   this.getFieldValue('ifrs-full:RoyaltyExpense', suffix) +
    //   // Employee benefits expense section (calculated subtotal)
    //   this.getFieldValue('ifrs-full:EmployeeBenefitsExpense', suffix) +
    //   // Director's remuneration section (calculated subtotal)
    //   this.getFieldValue('ssmt-mfrs:DirectorsRemuneration', suffix) +
    //   // Final field
    //   this.getFieldValue('ssmt-mfrs:OtherMiscellaneousExpenses', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:OtherExpenseByFunction' + suffix)
    //   ?.setValue(totalOtherExpenses, { emitEvent: false });
    // // 16. Total finance income
    // const totalFinanceIncome =
    //   this.getFieldValue(
    //     'ssmt-mfrs:InterestIncomeDueFromRelatedParties',
    //     suffix
    //   ) + this.getFieldValue('ifrs-full:OtherFinanceIncome', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-full:FinanceIncome' + suffix)
    //   ?.setValue(totalFinanceIncome, { emitEvent: false });
  }

  private calculateMPERSTotals(suffix: string): void {
    // ======================== REVENUE CALCULATIONS ========================
    // // 1. Total revenue from sale of goods
    // const totalRevenueFromSaleOfGoods =
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromSaleOfTelecommunicationEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:RevenueFromSaleOfProperty', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromSaleOfConstructionContracts',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromSaleOfCleanWaterTreatmentAndDisposalOfWasteWater',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromSaleOfFoodAndBeverage',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromSaleOfAgriculturalProduce',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromSaleOfOilAndGasProducts',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:RevenueFromSaleOfOtherGoods', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-smes:RevenueFromSaleOfGoods' + suffix)
    //   ?.setValue(totalRevenueFromSaleOfGoods, { emitEvent: false });
    // // 2. Total revenue from rendering of services
    // const totalRevenueFromServices =
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromEntertainmentOperations',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromRenderingOfTelecommunicationServices',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromRenderingOfTransportServices',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromRenderingOfInformationTechnologyServices',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:RevenueFromEducationalServices', suffix) +
    //   this.getFieldValue('ssmt-mpers:RevenueFromHealthcareServices', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromRenderingOfCargoAndMailTransportServices',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RevenueFromRenderingOfOtherServices',
    //     suffix
    //   );
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-smes:RevenueFromRenderingOfServices' + suffix)
    //   ?.setValue(totalRevenueFromServices, { emitEvent: false });
    // // 3. Total interest income
    // const totalInterestIncome =
    //   this.getFieldValue(
    //     'ssmt-mpers:InterestIncomeOnLoansAndReceivables',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:InterestIncomeOnOtherFinancialAssets',
    //     suffix
    //   );
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-smes:RevenueFromInterest' + suffix)
    //   ?.setValue(totalInterestIncome, { emitEvent: false });
    // // 4. Total other fee and commission income
    // const totalFeeAndCommission =
    //   this.getFieldValue('ssmt-mpers:BrokerageFeeIncome', suffix) +
    //   this.getFieldValue('ssmt-mpers:OtherFeesAndCommissionIncome', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:PortfolioAndOtherManagementFeeIncome',
    //     suffix
    //   );
    // this.analysisOfProfitOrLossForm
    //   .get('ssmt-mpers:FeeAndCommissionIncome' + suffix)
    //   ?.setValue(totalFeeAndCommission, { emitEvent: false });
    // // 5. Total revenue
    // const totalRevenue =
    //   this.getFieldValue('ifrs-smes:RevenueFromSaleOfGoods', suffix) +
    //   this.getFieldValue('ifrs-smes:RevenueFromRenderingOfServices', suffix) +
    //   this.getFieldValue('ifrs-smes:RevenueFromInterest', suffix) +
    //   this.getFieldValue('ssmt-mpers:FeeAndCommissionIncome', suffix) +
    //   this.getFieldValue('ifrs-smes:RevenueFromDividends', suffix) +
    //   this.getFieldValue('ssmt-mpers:RentalIncome', suffix) +
    //   this.getFieldValue('ifrs-smes:RevenueFromRoyalties', suffix) +
    //   this.getFieldValue('ifrs-smes:OtherRevenue', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-smes:Revenue' + suffix)
    //   ?.setValue(totalRevenue, { emitEvent: false });
    // // ======================== COST OF SALES ========================
    // // 6. Total cost of sales
    // const totalCostOfSales =
    //   this.getFieldValue('ssmt-mpers:CostOfInventories', suffix) +
    //   this.getFieldValue('ssmt-mpers:ConstructionContractCost', suffix) +
    //   this.getFieldValue('ssmt-mpers:EnergyExpense', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:PropertyDevelopmentAndProjectManagementExpense',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:OtherCostOfSales', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-smes:CostOfSales' + suffix)
    //   ?.setValue(totalCostOfSales, { emitEvent: false });
    // // ======================== OTHER INCOME ========================
    // // 7. Total foreign exchange gain
    // const totalForeignExchangeGain =
    //   this.getFieldValue('ssmt-mpers:RealisedGainOnForeignExchange', suffix) +
    //   this.getFieldValue('ssmt-mpers:UnrealisedGainOnForeignExchange', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ssmt-mpers:GainsOnForeignExchange' + suffix)
    //   ?.setValue(totalForeignExchangeGain, { emitEvent: false });
    // // 8. Total gain on disposal
    // const totalGainOnDisposal =
    //   this.getFieldValue('ssmt-mpers:GainsFromDisposalOfSubsidiaries', suffix) +
    //   this.getFieldValue('ssmt-mpers:GainsFromDisposalOfAssociates', suffix) +
    //   this.getFieldValue('ssmt-mpers:GainsFromDisposalOfJointVentures', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get(
    //     'ssmt-mpers:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates' +
    //       suffix
    //   )
    //   ?.setValue(totalGainOnDisposal, { emitEvent: false });
    // // 9. Total reversal of impairment loss
    // const totalReversalOfImpairment =
    //   this.getFieldValue(
    //     'ssmt-mpers:ReversalOfImpairmentLossRecognisedInProfitOrLossLoansAndAdvances',
    //     suffix
    //   ) +
    //   this.getFieldValue('ifrs-smes:ReversalOfInventoryWritedown', suffix) +
    //   this.getFieldValue(
    //     'ifrs-smes:ReversalOfImpairmentLossRecognisedInProfitOrLossIntangibleAssetsOtherThanGoodwill',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ReversalOfImpairmentLossRecognisedInProfitOrLossGoodwill',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-smes:ReversalOfImpairmentLossRecognisedInProfitOrLossPropertyPlantAndEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ReversalOfImpairmentLossRecognisedInProfitOrLossAssociates',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ReversalOfImpairmentLossRecognisedInProfitOrLossJointVentures',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ReversalOfImpairmentLossForOtherAssets',
    //     suffix
    //   );
    // this.analysisOfProfitOrLossForm
    //   .get(
    //     'ifrs-smes:ReversalOfImpairmentLossRecognisedInProfitOrLoss' + suffix
    //   )
    //   ?.setValue(totalReversalOfImpairment, { emitEvent: false });
    // // 10. Total other income
    // const totalOtherIncome =
    //   // First 3 fields
    //   this.getFieldValue('ssmt-mpers:AmortisationOfDeferredIncome', suffix) +
    //   this.getFieldValue('ssmt-mpers:BadDebtsRecovered', suffix) +
    //   this.getFieldValue('ssmt-mpers:OtherIncomeDividend', suffix) +
    //   // Foreign exchange gain section (calculated subtotal)
    //   this.getFieldValue('ssmt-mpers:GainsOnForeignExchange', suffix) +
    //   // Gain on disposal section (calculated subtotal + 4 more fields)
    //   this.getFieldValue(
    //     'ssmt-mpers:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:GainsOnDisposalOfOtherInvestments',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:GainsOnDisposalsOfPropertyPlantAndEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:GainsOnDisposalsOfNoncurrentAssets',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:OtherGainsRecognisedInProfitOrLossFairValueMeasurementAssets',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:OtherInterestIncome', suffix) +
    //   this.getFieldValue('ssmt-mpers:IncomeFromManagementFees', suffix) +
    //   this.getFieldValue(
    //     'ifrs-smes:GainsLossesOnFinancialAssetsAtFairValueThroughProfitOrLoss',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:FairValueGainOnRecycleOfForexReserveUponDisposalOfSubsdiaries',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:MiscellaneousOtherFeesAndCommissionIncome',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:OtherRentalIncome', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:OtherRentalIncomeOnLandAndBuildings',
    //     suffix
    //   ) +
    //   // Reversal of impairment section (calculated subtotal - already includes ReversalOfInventoryWritedown)
    //   this.getFieldValue(
    //     'ifrs-smes:ReversalOfImpairmentLossRecognisedInProfitOrLoss',
    //     suffix
    //   ) +
    //   // Fields after reversal section
    //   this.getFieldValue('ssmt-mpers:OtherRoyaltyIncome', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:GrantsOrIncentivesReceivedByMalaysianGovernmentOrAgencies',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:GrantsOrIncentivesReceivedByForeignGovernmentOrAgencies',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ContributionsReceivedByLocalContributor',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ContributionsReceivedByForeignContributor',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ContributionsReceivedByUnknownContributor',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:OtherMiscellaneousIncome', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-smes:OtherIncome' + suffix)
    //   ?.setValue(totalOtherIncome, { emitEvent: false });
    // // ======================== EXPENSES ========================
    // // 11. Total auditor's remuneration
    // const totalAuditorsRemuneration =
    //   this.getFieldValue(
    //     'ssmt-mpers:AuditorsRemunerationForAuditServices',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:AuditorsRemunerationForOtherServices',
    //     suffix
    //   );
    // this.analysisOfProfitOrLossForm
    //   .get('ssmt-mpers:AuditorsRemuneration' + suffix)
    //   ?.setValue(totalAuditorsRemuneration, { emitEvent: false });
    // // 12. Total loss on disposal
    // const totalLossOnDisposal =
    //   this.getFieldValue('ssmt-mpers:LossOnDisposalOfSubsidiaries', suffix) +
    //   this.getFieldValue('ssmt-mpers:LossOnDisposalOfAssociates', suffix) +
    //   this.getFieldValue('ssmt-mpers:LossOnDisposalOfJointVentures', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get(
    //     'ssmt-mpers:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures' +
    //       suffix
    //   )
    //   ?.setValue(totalLossOnDisposal, { emitEvent: false });
    // // 13. Total employee benefits expense
    // const totalEmployeeBenefits =
    //   this.getFieldValue('ifrs-smes:WagesAndSalaries', suffix) +
    //   this.getFieldValue('ssmt-mpers:BonusExpenses', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ExpenseFromSharebasedPaymentTransactionsWithEmployees',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:EmployeeShareBasedPayment', suffix) +
    //   this.getFieldValue('ifrs-smes:SocialSecurityContributions', suffix) +
    //   this.getFieldValue('ifrs-smes:TerminationBenefitsExpense', suffix) +
    //   this.getFieldValue(
    //     'ifrs-smes:PostemploymentBenefitExpenseDefinedContributionPlans',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ifrs-smes:PostemploymentBenefitExpenseDefinedBenefitPlans',
    //     suffix
    //   ) +
    //   this.getFieldValue('ifrs-smes:OtherLongtermBenefits', suffix) +
    //   this.getFieldValue('ifrs-smes:OtherShorttermEmployeeBenefits', suffix) +
    //   this.getFieldValue('ifrs-smes:OtherEmployeeExpense', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-smes:EmployeeBenefitsExpense' + suffix)
    //   ?.setValue(totalEmployeeBenefits, { emitEvent: false });
    // // 14. Total director's remuneration
    // const totalDirectorsRemuneration =
    //   this.getFieldValue('ssmt-mpers:WagesAndSalariesToDirectors', suffix) +
    //   this.getFieldValue('ssmt-mpers:BonusToDirectors', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:KeyManagementPersonnelCompensationSharebasedPayment',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:BenefitsInKindExpenses', suffix) +
    //   this.getFieldValue('ssmt-mpers:DirectorsFees', suffix) +
    //   this.getFieldValue('ssmt-mpers:PerformanceIncentives', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RetirementBenefitsDefinedContributionPlans',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:RetirementBenefitsDefinedBenefitPlans',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:OtherDirectorsRemuneration', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ssmt-mpers:DirectorsRemuneration' + suffix)
    //   ?.setValue(totalDirectorsRemuneration, { emitEvent: false });
    // // 15. Total other expenses
    // const totalOtherExpenses =
    //   // Auditor's remuneration section (calculated subtotal)
    //   this.getFieldValue('ssmt-mpers:AuditorsRemuneration', suffix) +
    //   // 3 fields after auditor's remuneration
    //   this.getFieldValue('ssmt-mpers:AmortisationExpense', suffix) +
    //   this.getFieldValue(
    //     'ifrs-smes:DepreciationPropertyPlantAndEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:NaturalDisasterRelatedExpenses', suffix) +
    //   // Loss on disposal section (calculated subtotal)
    //   this.getFieldValue(
    //     'ssmt-mpers:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures',
    //     suffix
    //   ) +
    //   this.getFieldValue('ssmt-mpers:LossesOnDisposalsOfInvestments', suffix) +
    //   this.getFieldValue(
    //     'ssmt-mpers:LossesOnDisposalsOfPropertyPlantAndEquipment',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:LossesOnDisposalsOfNoncurrentAssets',
    //     suffix
    //   ) +
    //   // 3 additional expense fields
    //   this.getFieldValue(
    //     'ssmt-mpers:LossOnRevaluationOfFinancialInstruments',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ProvisionForLiabilitiesAndCharges',
    //     suffix
    //   ) +
    //   this.getFieldValue(
    //     'ssmt-mpers:ProvisionForGuaranteeAndWarranties',
    //     suffix
    //   ) +
    //   // Rental and royalty fields
    //   this.getFieldValue('ssmt-mpers:RentalExpense', suffix) + // 130
    //   this.getFieldValue('ssmt-mpers:RoyaltyExpense', suffix) + // 131
    //   // Employee benefits expense section (calculated subtotal)
    //   this.getFieldValue('ifrs-smes:EmployeeBenefitsExpense', suffix) +
    //   // Director's remuneration section (calculated subtotal)
    //   this.getFieldValue('ssmt-mpers:DirectorsRemuneration', suffix) +
    //   // Final field
    //   this.getFieldValue('ssmt-mpers:OtherMiscellaneousExpenses', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-smes:OtherExpenseByFunction' + suffix)
    //   ?.setValue(totalOtherExpenses, { emitEvent: false });
    // // 16. Total finance income
    // const totalFinanceIncome =
    //   this.getFieldValue(
    //     'ssmt-mpers:InterestIncomeDueFromRelatedParties',
    //     suffix
    //   ) + this.getFieldValue('ssmt-mpers:OtherFinanceIncome', suffix);
    // this.analysisOfProfitOrLossForm
    //   .get('ifrs-smes:FinanceIncome' + suffix)
    //   ?.setValue(totalFinanceIncome, { emitEvent: false });
  }

  private calculateTotals(): void {
    if (!this.schema) return;

    this.isCalculating = true;

    // Get taxonomy prefix (ifrs-full for MFRS, ifrs-smes for MPERS)
    const prefix = this.getXbrlPrefix() as 'ifrs-full' | 'ifrs-smes';

    // Get all current form values
    const allFormData = this.analysisOfProfitOrLossForm.getRawValue();

    // Call the calculation service
    const calculatedTotals = this.calculationService.calculateTotals_310100(
      allFormData,
      prefix
    );

    // Register calculated keys so isSheetModified() skips them
    this.calculatedTotalKeys = Object.keys(calculatedTotals);
    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.analysisOfProfitOrLoss,
      Object.keys(calculatedTotals)
    );

    // Apply calculated values to form controls
    Object.entries(calculatedTotals).forEach(([controlName, value]) => {
      const control = this.analysisOfProfitOrLossForm.get(controlName);
      if (control) {
        control.setValue(value, { emitEvent: false });
        // Mark as pristine to prevent calculated fields from showing 'Edited' status
        control.markAsPristine();
      }
    });

    this.isCalculating = false;
  }

  /**
   * Check if there are actual validation errors (excluding API mismatch "Suggested Change" warnings).
   * API mismatch warnings contain "does not match the extracted sum" and are not real errors.
   */
  private hasActualValidationErrors(): boolean {
    for (const errors of this.validationErrors.values()) {
      const hasRealError = errors.some(
        (error) => !error.includes('does not match the extracted sum')
      );
      if (hasRealError) return true;
    }
    return false;
  }

  /**
   * Validate calculated totals against API baseline data
   * Compares calculated sums with the original API values to detect discrepancies
   */
  private validateAgainstApiData(): void {
    // Skip validation during cancel flow - form is reverting to original values
    if (this.isCancellingChanges) {
      return;
    }

    // Compare against the immutable N8N snapshot (extracted values), NOT the last-submission
    // baseline — so "Validated*" means "differs from what N8N extracted", consistent with the
    // other migrated sheets. Empty pre-rollout; the guard below then skips the check.
    const originalApiData = this.mappingStatus.getSnapshotValues(SHEET_KEYS.analysisOfProfitOrLoss);

    if (!originalApiData || Object.keys(originalApiData).length === 0) {
      return;
    }

    // Narrowed scope: only calculated/readonly monetary totals are compared against the
    // immutable snapshot. Delegating to the shared ExtractedSumWarningService keeps this
    // behaviour aligned across migrated sheets (role12, role13, ...) — see
    // ExtractedSumWarningService.computeMismatchWarnings for the support/calculated gate.
    const columns: { key: string; label: string }[] = [];
    if (this.showConsolidatedColumns)                         columns.push({ key: 'Consolidated|CurrentYear',  label: 'Consolidated Current' });
    if (this.showConsolidatedColumns && this.hasPreviousYear) columns.push({ key: 'Consolidated|PreviousYear', label: 'Consolidated Previous' });
    columns.push({ key: 'Separate|CurrentYear', label: 'Separate Current' });
    if (this.hasPreviousYear)                                 columns.push({ key: 'Separate|PreviousYear', label: 'Separate Previous' });

    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.analysisOfProfitOrLoss,
      controls: this.schema?.controls ?? [],
      columns,
      getControlName: (qname, key) => {
        const [cs, period] = key.split('|');
        return `${qname}[${cs}][${period}]`;
      },
      getCurrentValue: (name) => this.analysisOfProfitOrLossForm.get(name)?.value,
    });

    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema?.controls ?? [], warnings);

    // STEP 2: Cross-sheet validation (MPERS only)
  }

  /**
   * SAME-SHEET validation — safe to run live during edit. Clears ONLY the
   * same-sheet partition, preserving frozen cross-sheet entries (those in
   * crossSheetErrorQnames) until the next Tick / cross-sheet listener re-runs
   * validateCrossSheet. Mirrors role 18's validateSameSheet so the deferred
   * inventory-equality error survives live keystrokes instead of being wiped.
   */
  private validateSameSheet(): void {
    for (const key of Array.from(this.validationErrors.keys())) {
      if (!this.crossSheetErrorQnames.has(key)) {
        this.validationErrors.delete(key);
      }
    }
    this.validateAgainstApiData();
    this.evaluateSheetStatus();
  }

  /**
   * CROSS-SHEET validation — deferred to Tick (saveEditMode) and the cross-sheet
   * listener, never run live per keystroke. Recomputes referenced sheets then
   * runs the schema-declared equality rules (MPERS-only inventory write-down ↔
   * Role 18 SOCF). Mirrors role 18's validateCrossSheet: strip prior cross-sheet
   * entries (preserving same-sheet extracted-sum warnings), rebuild
   * equalityValidationErrors + validationErrors + crossSheetErrorQnames. No-op
   * for MFRS (the MFRS schema declares no equality) and when Role 18 is absent.
   */
  private validateCrossSheet(): void {
    if (!this.schema) return;

    // Remove previous cross-sheet entries from validationErrors.
    for (const qname of this.crossSheetErrorQnames) {
      this.validationErrors.delete(qname);
    }
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.clear();

    const currentTaxonomy: 'MFRS' | 'MPERS' = this.crossRefService.isMFRS() ? 'MFRS' : 'MPERS';

    const allSheetData = this.stateService.exportAllData();
    const referenced = referencedSheetKeysFromSchema(this.schema as any, SHEET_KEYS.analysisOfProfitOrLoss);
    const formValuesWithAllSheets = recomputeReferencedSheets(
      { ...allSheetData, [SHEET_KEYS.analysisOfProfitOrLoss]: this.analysisOfProfitOrLossForm.getRawValue() },
      referenced,
      currentTaxonomy,
      (key) => this.validationContext.getCachedSchema(this.validationContext.resolveSheetKey(key) ?? key)
    );

    const errors = this.equalityValidation.validateAllEqualityRules(
      this.schema as any,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.analysisOfProfitOrLoss
    );

    Object.keys(errors).forEach((qname) => {
      this.equalityValidationErrors.set(qname, errors[qname]);
      const errorMessages = errors[qname].map((err: EqualityValidationError) => err.message);
      const existingErrors = this.validationErrors.get(qname) || [];
      // Keep same-sheet suggested-change warnings; replace the cross-sheet entries.
      const nonCrossErrors = existingErrors.filter((msg) => msg.includes(EXTRACTED_SUM_MARKER));
      this.validationErrors.set(qname, [...nonCrossErrors, ...errorMessages]);
      this.crossSheetErrorQnames.add(qname);
    });
  }

  /**
   * Run all validation methods, then update sheet-level status.
   * Centralizes validation so callers don't need to remember which validators to run.
   */
  private runAllValidations(): void {
    this.validateSameSheet();
    this.validateCrossSheet();
    this.evaluateSheetStatus();
  }

  /**
   * Get XBRL prefix based on whether we're using MFRS or MPERS
   */
  private getXbrlPrefix(): string {
    const isMFRS = this.crossRefService.isMFRS();
    const prefix = isMFRS ? 'ifrs-full' : 'ifrs-smes';
    return prefix;
  }

  /**
   * Get field value by qname and suffix
   */
  private getFieldValue(qname: string, suffix: string): number {
    const controlName = qname + suffix;
    const control = this.analysisOfProfitOrLossForm.get(controlName);

    if (!control) {
      return 0;
    }

    const value = control.value;

    // IMPORTANT: Convert to number to prevent string concatenation (e.g., "1" + "1" = "11")
    // parseFloat handles null/undefined by returning NaN, so we use || 0 as fallback
    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? 0 : numericValue;
  }

  /**
   * Filter out calculated fields from data object
   * Calculated fields should NEVER be loaded from database/saved state - always calculated fresh
   * This ensures database values (e.g., 1488) don't overwrite calculated values (e.g., 62.05)
   */
  private filterOutCalculatedFields(
    data: Record<string, any>
  ): Record<string, any> {
    if (!this.schema) return data;

    const filteredData: Record<string, any> = {};
    // IMPORTANT: Use bracket notation to match form control names and state data keys
    const suffixes = [
      '[Consolidated][CurrentYear]',
      '[Consolidated][PreviousYear]',
      '[Separate][CurrentYear]',
      '[Separate][PreviousYear]',
    ];

    // Get all calculated controls from schema
    const calculatedQnames = this.schema.controls
      .filter(
        (control) =>
          control.ui.calculated === true || control.ui.readonly === true
      )
      .map((control) => control.qname);

    // Build a set of all calculated field keys for fast lookup
    const calculatedKeys = new Set<string>();
    for (const qname of calculatedQnames) {
      for (const suffix of suffixes) {
        calculatedKeys.add(`${qname}${suffix}`);
      }
    }

    // Filter out calculated fields
    Object.keys(data).forEach((key) => {
      if (!calculatedKeys.has(key)) {
        filteredData[key] = data[key];
      }
    });
    return filteredData;
  }

  /**
   * Setup listener for external state updates (e.g., when backend API data is populated)
   */
  private setupStateListener(): void {
    this.stateService.modifiedState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        // Skip re-validation when this component triggered the state update itself
        if (this.isStateSelfUpdate) {
          return;
        }

        // After initial load completes, skip re-patching. Past this point the
        // only emissions on this slice are commits — this sheet's own (already
        // dropped by `isStateSelfUpdate` above) and, via
        // `FilingFormStateService.applyRemoteChange`, a sibling tab's. A
        // re-patch here would overwrite the typed-but-unsaved values of rows
        // the user has OPEN (inline edit-mode sheets commit on Tick, so those
        // edits live only in the form while state still holds the pre-edit
        // number). Adopting a sibling's values is `adoptRemoteState()`'s job —
        // it runs later, on `remoteUpdateApplied$`, and skips open rows.
        // Mirrors role23/role24. See isInitialLoadComplete.
        if (this.isInitialLoadComplete) {
          return;
        }

        const newData = state.analysisOfProfitOrLoss;

        // Only reload if we have new data AND form is not currently being edited
        // Also skip during cancel flow to prevent chain reactions of validation
        if (
          newData &&
          Object.keys(newData).length > 0 &&
          !this.isLoadingState &&
          !this.isCancellingChanges
        ) {
          this.isLoadingState = true;

          // CRITICAL: Filter out calculated fields before patching
          // Calculated fields should NEVER be loaded from database - always calculated fresh
          const filteredData = this.filterOutCalculatedFields(newData);

          this.analysisOfProfitOrLossForm.patchValue(filteredData, {
            emitEvent: false,
          });

          // Verify data was actually patched
          const afterPatch = this.analysisOfProfitOrLossForm.getRawValue();
          const sampleAfter = Object.entries(afterPatch)
            .filter(([k, v]) => v !== 0 && v !== null)
            .slice(0, 5);

          // Recalculate totals and validate
          setTimeout(() => {
            this.calculateTotals();
            this.runAllValidations();

            // CRITICAL: Save calculated values to state after recalculation
            // This ensures Role 12 can pull the correct calculated values (e.g., 62.05), not DB values (e.g., 1488)
            const allFormData = this.analysisOfProfitOrLossForm.getRawValue();
            // Skip marking as edited - this is triggered by state listener during external updates
            this.isStateSelfUpdate = true;
            this.stateService.updateSheetData(
              SHEET_KEYS.analysisOfProfitOrLoss,
              allFormData,
              true,  // isValid
              true   // skipEditedMark - don't mark as edited during state sync
            );
            this.isStateSelfUpdate = false;

            this.isLoadingState = false;
            this.isValidationComplete = true;
          }, 100);
        }
      });

    // ----------------------------------
    // Cross-sheet local re-validation watch (golden pattern, role-19).
    //
    // Role-13 reads both filing-info slots through `dimensionVisibility` (the
    // shared resolver decides which columns exist) and scopeOfFiling in
    // `loadSchema`'s taxonomy pick, AND (MPERS) Role 18 SOCF for the inventory
    // write-down equality. Same-tab SPA navigation back to this sheet — or a
    // sibling Role 18 edit — should re-validate when any upstream slice changed.
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.analysisOfProfitOrLoss)),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
          r2: state.scopeOfFiling,
          r18: state.statementOfCashFlowsIndirectMethod,
        })),
        distinctUntilChanged(),
        debounceTime(CROSS_SHEET_VALIDATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.isLoadingState) {
          this.runAllValidations();
        }
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab safety net — sibling-tab edits to filingPrerequisites or
    // scopeOfFiling must trigger re-validation here too.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.analysisOfProfitOrLoss)),
        filter(() => !!this.schema && !!this.analysisOfProfitOrLossForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey, changedSheets }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role13-analysisOfProfitOrLoss',
          //   sourceSheetKey: sheetKey,
          // });
        }

        // Own-slice change -> repaint this sheet's fields. Other-slice change ->
        // fall through to the cross-sheet revalidation tail only, as before.
        if (changedSheets.includes(SHEET_KEYS.analysisOfProfitOrLoss))
          this.adoptRemoteState();

        this.runAllValidations();
        this.evaluateSheetStatus();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption for role13 (analysis of profit or loss). Rows currently
   * open in edit mode are skipped — the user's in-flight work wins until they
   * tick (local overwrites and broadcasts) or press X (the row lands on the
   * adopted value; see `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   * `calculateTotals()` here recomputes for DISPLAY only; the sibling already
   * persisted its own recomputed totals.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.analysisOfProfitOrLossForm,
      this.stateService.getSheetData(SHEET_KEYS.analysisOfProfitOrLoss),
      openControlNames
    );

    this.calculateTotals();
    this.validateSameSheet();
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.analysisOfProfitOrLoss);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.analysisOfProfitOrLoss);

    if (savedData && Object.keys(savedData).length > 0) {
      // CRITICAL: Filter out calculated fields before patching
      // Calculated fields should NEVER be loaded from saved state - always calculated fresh
      const filteredData = this.filterOutCalculatedFields(savedData);

      this.analysisOfProfitOrLossForm.patchValue(filteredData, {
        emitEvent: false,
      });

      // Recalculate totals and validate after loading
      setTimeout(() => {
        this.calculateTotals();
        this.runAllValidations();

        // CRITICAL: Save calculated values to state after recalculation
        // This ensures Role 12 can pull the correct calculated values (e.g., 62.05), not stale values
        const allFormData = this.analysisOfProfitOrLossForm.getRawValue();

        // Skip marking as edited during initial load - this is just saving calculated values
        this.isStateSelfUpdate = true;
        this.stateService.updateSheetData(
          SHEET_KEYS.analysisOfProfitOrLoss,
          allFormData,
          false,
          true   // skipEditedMark - don't mark as edited during initial load
        );
        this.isStateSelfUpdate = false;

        this.isLoadingState = false;
        this.isValidationComplete = true;

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.analysisOfProfitOrLoss);
          // Initial load has settled — from here the state listener stops
          // re-patching so a foreign commit can't clobber in-edit rows.
          this.isInitialLoadComplete = true;
        }, 600);
      }, 100);
    } else {
      // Still calculate totals so calculated fields show 0.00 instead of blank
      setTimeout(() => {
        this.calculateTotals();
        this.isLoadingState = false;
        this.isValidationComplete = true;

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.analysisOfProfitOrLoss);
          // Initial load has settled — from here the state listener stops
          // re-patching so a foreign commit can't clobber in-edit rows.
          this.isInitialLoadComplete = true;
        }, 600);
      }, 100);
    }
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.analysisOfProfitOrLossForm.valid) {
      const formData = this.analysisOfProfitOrLossForm.value;

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Analysis of profit or loss data saved successfully',
        life: TOAST_DURATION_DEFAULT_MS,
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please check all required fields',
        life: TOAST_DURATION_DEFAULT_MS,
      });
    }
  }

  /**
   * Bound as a property, not a method reference, so the directive receives a
   * stable function identity — a `[fieldLinkIsSectionHeader]="isSectionHeader"`
   * binding would pass an unbound method whose `this` is undefined when called.
   */
  protected readonly isSectionHeaderFn = (control: any): boolean =>
    this.isSectionHeader(control);

  /**
   * Check if a row is currently in edit mode
   */
  isRowInEditMode(control: FieldControl): boolean {
    return this.editingRows.has(control.qname);
  }

  /**
   * Enter edit mode for a row - store original values, enable controls, and mark as editing
   */
  enterEditMode(control: FieldControl): void {
    // Don't allow editing before validation is complete
    if (!this.isValidationComplete) {
      return;
    }
    // Don't allow editing calculated or readonly fields
    if (this.isCalculatedField(control) || control.ui.readonly) {
      return;
    }

    // Capture the committed badge BEFORE entering edit mode (isRowInEditMode is still false here).
    this.frozenRowStatus.set(control.qname, this.getRowValidationStatus(control));

    // Get all 4 controls for this row
    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous'),
    ];

    // Store original values
    const originalValues: { [key: string]: number | null } = {};
    controlNames.forEach((name) => {
      originalValues[name] =
        this.analysisOfProfitOrLossForm.get(name)?.value ?? null;
    });
    this.editingRows.set(control.qname, originalValues);
    this.stateService.markSheetEditing(SHEET_KEYS.analysisOfProfitOrLoss);

    // Enable all 4 controls (don't emit events to prevent triggering auto-save),
    // revealing only the columns this permutation SHOWS — a hidden required column
    // is permanently null, so enabling makes it invalid and revealing it would
    // paint the row red with no text to explain it.
    enableDualColumnRowAndRevealVisible(
      this.analysisOfProfitOrLossForm,
      control.qname,
      this.dimensionVisibility
    );
  }

  /**
   * Save edit mode - disable controls and mark as pristine.
   * "Edited" status is now derived by MappingStatusService (last-submission comparison)
   * rather than a per-sheet baseline marker.
   */
  saveEditMode(control: FieldControl): void {
    if (!this.editingRows.has(control.qname)) {
      return;
    }

    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous'),
    ];

    // Mark controls as pristine and disable them (don't emit events to prevent triggering auto-save)
    controlNames.forEach((name) => {
      const formControl = this.analysisOfProfitOrLossForm.get(name);
      if (formControl) {
        formControl.markAsPristine();
        formControl.disable({ emitEvent: false });
      }
    });

    // Remove from editing set
    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);

    // Recalculate totals FIRST so the persisted subset includes the freshly
    // recalculated calculated/derived totals (downstream sheets read these).
    this.calculateTotals();

    const subset = pickSubset(this.analysisOfProfitOrLossForm.getRawValue(), [...controlNames, ...this.calculatedTotalKeys]);
    this.isStateSelfUpdate = true;
    this.stateService.updateSheetData(SHEET_KEYS.analysisOfProfitOrLoss, subset);
    this.isStateSelfUpdate = false;

    this.validateSameSheet();
    // Cross-sheet (MPERS inventory ↔ Role 18) is deferred to Tick — re-run it on
    // commit so the red border / Error Description appear once the edit lands.
    this.validateCrossSheet();
    this.stateService.reconcileEditedFlag(SHEET_KEYS.analysisOfProfitOrLoss);
    this.stateService.clearSheetEditing(SHEET_KEYS.analysisOfProfitOrLoss);
    this.evaluateSheetStatus();
  }

  /**
   * Cancel edit mode - always revert to the snapshot captured on enterEditMode,
   * then disable controls. "Edited" status is derived by MappingStatusService,
   * so no per-row marker needs to be managed here.
   */
  cancelEditMode(control: FieldControl): void {
    const originalValues = this.editingRows.get(control.qname);
    if (!originalValues) {
      return;
    }

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const adopted = this.stateService.getSheetData(SHEET_KEYS.analysisOfProfitOrLoss);
    Object.keys(originalValues).forEach((name) => {
      const formControl = this.analysisOfProfitOrLossForm.get(name);
      if (formControl) {
        const revertValue = name in adopted
          ? adopted[name]
          : (originalValues[name] ?? null);
        formControl.setValue(revertValue, { emitEvent: false });
        formControl.markAsPristine();
        formControl.disable({ emitEvent: false });
      }
    });

    // Remove from editing set
    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);

    // Recalculate totals to ensure consistency
    this.calculateTotals();
    this.validateSameSheet();
    this.stateService.clearSheetEditing(SHEET_KEYS.analysisOfProfitOrLoss);
    this.evaluateSheetStatus();
  }

  /**
   * Get validation status for a row (checks all 4 columns).
   * Delegates to MappingStatusService.computeRowStatus for Edited/Validated* resolution;
   * preserves all existing Failed-branch inputs (form invalidity, mandatory).
   * Status priority: Edited > Failed > Validated* > Validated
   */
  getRowValidationStatus(
    control: FieldControl
  ): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    // Only check monetary items
    if (control.ui.controlType !== 'number') return 'Validated';

    if (this.isRowInEditMode(control)) {
      const frozen = this.frozenRowStatus.get(control.qname);
      if (frozen !== undefined) return frozen;
    }

    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];
    const controlNames = columns.map((column) =>
      this.getXbrlControlName(control.qname, column.consolidatedOrSeparate, column.period)
    );

    // Gather current values for the row (the Failed-branch inputs — form
    // invalidity, mandatory, validation errors — are derived in
    // _computeRowErrorFlags so the badge and the row colour intent share them).
    const currentValues: Record<string, unknown> = {};
    for (const controlName of controlNames) {
      const fc = this.analysisOfProfitOrLossForm.get(controlName);
      if (fc) {
        currentValues[controlName] = fc.value;
      }
    }

    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control);

    return this.mappingStatus.computeRowStatus({
      controlNames,
      currentValues,
      lastSubmissionValues: this.mappingStatus.getLastSubmissionValues(
        SHEET_KEYS.analysisOfProfitOrLoss
      ),
      hasError,
      hasSuggestedChangeWarning,
      forceEdited: false,
      // Calculated/readonly totals are computed client-side and are absent from the
      // persisted baseline, so the deviation check would mark them Edited with zero
      // user edits. They are never user-editable (enterEditMode skips
      // calculated || readonly) — mark them derived to skip the Edited branch.
      // (Mirrors the calculated-set definition used elsewhere in this sheet:
      // ui.calculated === true || ui.readonly === true.)
      derived: !!(control.ui.calculated || control.ui.readonly),
    });
  }

  /**
   * Compute the row's hasError / hasSuggestedChangeWarning flags. This is the
   * single definition of "does the row have a hard error" vs "only a
   * suggested-change warning" — consumed by both the Mapping Status (which
   * layers the Edited/forceEdited override on top) and the row colour intent
   * (which deliberately ignores edit mode). Keeping it in one place means the
   * red/yellow background can never disagree with the Failed/Validated* badge.
   *
   * The logic here is the verbatim extraction of what previously lived inline
   * in getRowValidationStatus(); behaviour is preserved.
   */
  private _computeRowErrorFlags(
    control: FieldControl
  ): { hasError: boolean; hasSuggestedChangeWarning: boolean } {
    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];
    const controlNames = columns.map((column) =>
      this.getXbrlControlName(control.qname, column.consolidatedOrSeparate, column.period)
    );

    // Only a VISIBLE column may paint the row. A column this permutation does not
    // render can never SHOW a message — every message surface on this sheet
    // filters hidden columns out — so counting one here produces a red row with an
    // empty Error Description. That is the UAT defect of 2026-08-26 (role 18's
    // "Cash and cash equivalents at beginning / end of period" rows, opened on a
    // filing with no previous year): `enable()` makes a hidden, permanently-null
    // required cell invalid, and the reveal on row-open then marks it touched.
    //
    // Developer Note (TODO): `hasValue` below is deliberately still read across
    // ALL FOUR columns. Narrowing it to the visible ones is a separate, correct
    // change — a mandatory row whose only value sits in a hidden column IS
    // unsatisfied — but it can only ADD red, so it must not land until this
    // sheet's required-message builder is confirmed to emit a per-visible-column
    // mandatory line for that case. Otherwise it recreates the very defect above,
    // in the other direction. Role 18 DOES narrow both halves — its
    // `validateRequiredFields()` was made the last writer of the per-column
    // mandatory partition on 2026-08-26, so its red is always accompanied by
    // text; that confirmation is what this sheet still owes.
    const columnVisible: boolean[] = [
      this.showConsolidatedColumns,
      this.showConsolidatedColumns && this.hasPreviousYear,
      true,
      this.hasPreviousYear,
    ];

    let hasInvalid = false;
    let hasValue = false;
    controlNames.forEach((controlName, index) => {
      const fc = this.analysisOfProfitOrLossForm.get(controlName);
      if (fc) {
        if (columnVisible[index] && fc.invalid && (fc.dirty || fc.touched)) hasInvalid = true;
        if (fc.value !== null && fc.value !== undefined && fc.value !== '') hasValue = true;
      }
    });

    const isRequired = control.required === true;
    let hasError = hasInvalid || (isRequired && !hasValue);
    if (!hasError && this.validationErrors.has(control.qname)) {
      const errors = this.validationErrors.get(control.qname) || [];
      hasError = errors.some((message) => !message.includes('does not match the extracted sum'));
    }

    // Suggested-Change warning: all errors are "does not match the extracted sum",
    // and field has no linkTo (linkTo fields are read-only inherited values).
    let hasSuggestedChangeWarning = false;
    if (this.validationErrors.has(control.qname)) {
      const allErrors = this.validationErrors.get(control.qname) || [];
      const allAreSuggestedChanges = allErrors.every((message) =>
        message.includes('does not match the extracted sum')
      );
      hasSuggestedChangeWarning = allAreSuggestedChanges && !control.ui.linkTo;
    }

    return { hasError, hasSuggestedChangeWarning };
  }

  /**
   * Compute the row background colour intent: 'error' (red) when the row has a
   * hard error, 'warning' (yellow) when it only has a suggested-change warning,
   * 'none' otherwise. Red outranks yellow. Unlike the Mapping Status this does
   * NOT consult edit mode, so clicking the edit pencil never repaints the row.
   *
   * Cross-sheet defence: hasCrossSheetValidationError() is folded into the
   * 'error' branch so any future cross-sheet condition keeps the row RED even
   * if it is not already covered by _computeRowErrorFlags' validationErrors
   * check. (Today the method is a no-op stub returning false, so this is a
   * safety guarantee, not a behaviour change.)
   */
  private _computeRowColorIntent(control: FieldControl): 'error' | 'warning' | 'none' {
    if (control.ui.controlType !== 'number') return 'none';
    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control);
    if (hasError || this.hasCrossSheetValidationError(control)) return 'error';
    if (hasSuggestedChangeWarning) return 'warning';
    return 'none';
  }

  /**
   * Row background colour intent (public, used by the template). 'error' → red
   * (`row-has-error`), 'warning' → yellow (`row-has-validation-error`), 'none'
   * → no highlight. Role 13 does not cache row status, so this returns the live
   * computation directly (no rowColorIntentCache, unlike Role 19).
   */
  getRowColorIntent(control: FieldControl): 'error' | 'warning' | 'none' {
    return this._computeRowColorIntent(control);
  }

  /**
   * Get error description for a row (aggregates from all 4 columns)
   * Groups errors by category: "Error" (red) and "* Suggested Change" (black)
   */
  getRowErrorDescription(control: FieldControl): string {
    // Only check monetary items
    if (control.ui.controlType !== 'number') return '';

    const errors: string[] = []; // Red - Error category (cross-sheet, form control)
    const suggestedChanges: string[] = []; // Black - * Suggested Change category (API mismatch)

    // Column visibility filter helper
    const isVisibleColumn = (error: string): boolean => {
      if (
        error.includes('Consolidated Current') &&
        !this.showConsolidatedColumns
      )
        return false;
      if (
        error.includes('Consolidated Previous') &&
        (!this.showConsolidatedColumns || !this.hasPreviousYear)
      )
        return false;
      if (error.includes('Separate Previous') && !this.hasPreviousYear)
        return false;
      return true;
    };

    // 1. Process validation errors from validationErrors Map
    if (this.validationErrors.has(control.qname)) {
      const allValidationErrors =
        this.validationErrors.get(control.qname) || [];

      allValidationErrors.forEach((errorMsg) => {
        if (!isVisibleColumn(errorMsg)) return;

        if (errorMsg.includes('does not match the extracted sum')) {
          suggestedChanges.push(errorMsg);
        } else {
          errors.push(errorMsg);
        }
      });
    }

    // 2. Collect form control validation errors -> Error category
    // Control name format must match getXbrlControlName: qname[Consolidated|Separate][CurrentYear|PreviousYear]
    const columns: Array<{
      consolidatedOrSeparate: string;
      period: string;
      label: string;
      show: boolean;
    }> = [
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'CurrentYear',
        label: 'Consolidated Current',
        show: this.showConsolidatedColumns,
      },
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'PreviousYear',
        label: 'Consolidated Previous',
        show: this.showConsolidatedColumns && this.hasPreviousYear,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'CurrentYear',
        label: 'Separate Current',
        show: true,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'PreviousYear',
        label: 'Separate Previous',
        show: this.hasPreviousYear,
      },
    ];

    for (const { consolidatedOrSeparate, period, label, show } of columns) {
      if (!show) continue;

      const controlName = `${control.qname}[${consolidatedOrSeparate}][${period}]`;
      const formControl = this.analysisOfProfitOrLossForm.get(controlName);

      if (
        formControl &&
        formControl.invalid &&
        (formControl.dirty || formControl.touched)
      ) {
        const errorMessages: string[] = [];

        if (formControl.errors) {
          if (formControl.errors['required'])
            errorMessages.push('Required field');
          if (formControl.errors['min'])
            errorMessages.push(`Min: ${formControl.errors['min'].min}`);
          if (formControl.errors['max'])
            errorMessages.push(`Max: ${formControl.errors['max'].max}`);
        }

        if (errorMessages.length > 0) {
          errors.push(`${label}: ${errorMessages.join(', ')}`);
        }
      }
    }

    // 3. Format output grouped by category with CSS classes
    // Error section in red (.error-text), Suggested Change section in black (.suggested-change-text)
    let result = '';

    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result +=
        '<span class="error-text">' +
        errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') +
        '</span>';
    }

    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result +=
        '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result +=
        '<span class="suggested-change-text">' +
        suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') +
        '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Columns + getControlName rebuilt inline to mirror computeExtractedSumMismatchWarnings'
    // computeMismatchWarnings call exactly (same visible columns + flat keys).
    if (this.baselineDebug.enabled()) {
      const columns: { key: string; label: string }[] = [];
      if (this.showConsolidatedColumns)                         columns.push({ key: 'Consolidated|CurrentYear',  label: 'Consolidated Current' });
      if (this.showConsolidatedColumns && this.hasPreviousYear) columns.push({ key: 'Consolidated|PreviousYear', label: 'Consolidated Previous' });
      columns.push({ key: 'Separate|CurrentYear', label: 'Separate Current' });
      if (this.hasPreviousYear)                                 columns.push({ key: 'Separate|PreviousYear', label: 'Separate Previous' });
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.analysisOfProfitOrLoss,
        control as unknown as SummableControl,
        columns,
        (qname, key) => {
          const [cs, period] = key.split('|');
          return `${qname}[${cs}][${period}]`;
        }
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Check if row has Error category items (for red text styling)
   * Error category includes: cross-sheet validation, form control errors
   */
  hasErrorCategory(control: FieldControl): boolean {
    const columns: Array<{ suffix: string; show: boolean }> = [
      { suffix: 'ConsolidatedCurrent', show: this.showConsolidatedColumns },
      {
        suffix: 'ConsolidatedPrevious',
        show: this.showConsolidatedColumns && this.hasPreviousYear,
      },
      { suffix: 'SeparateCurrent', show: true },
      { suffix: 'SeparatePrevious', show: this.hasPreviousYear },
    ];

    for (const { suffix, show } of columns) {
      if (!show) continue;

      const controlName = control.qname + suffix;
      const formControl = this.analysisOfProfitOrLossForm.get(controlName);

      if (
        formControl &&
        formControl.invalid &&
        (formControl.dirty || formControl.touched)
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if row has Suggested Change category items (for black text styling)
   * Suggested Change category includes: API mismatch warnings
   */
  hasSuggestedChangeCategory(control: FieldControl): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    const allErrors = this.validationErrors.get(control.qname) || [];

    return allErrors.some((errorMsg) => {
      if (
        errorMsg.includes('Consolidated Current') &&
        !this.showConsolidatedColumns
      )
        return false;
      if (
        errorMsg.includes('Consolidated Previous') &&
        (!this.showConsolidatedColumns || !this.hasPreviousYear)
      )
        return false;
      if (errorMsg.includes('Separate Previous') && !this.hasPreviousYear)
        return false;

      return errorMsg.includes('does not match the extracted sum');
    });
  }

  /**
   * Check if row has failed validation
   */
  hasRowFailed(control: FieldControl): boolean {
    return this.getRowValidationStatus(control) === 'Failed';
  }

  /**
   * Evaluate sheet status by looping through all rows' mapping status.
   * Sets hasErrors = true if any row has 'Failed' status, false otherwise.
   * Called at init and before submit.
   */
  evaluateSheetStatus(): void {
    if (!this.schema?.controls) return;

    let hasErrors = false;
    for (const control of this.schema.controls) {
      if (this.getRowValidationStatus(control) === 'Failed') {
        hasErrors = true;
        break;
      }
    }

    this.stateService.updateSheetLevelStatus(SHEET_KEYS.analysisOfProfitOrLoss, { hasErrors });
  }

  /**
   * Check if row has validation error (for yellow highlighting)
   * Checks validationErrors Map directly instead of status
   * Excludes cross-sheet validation errors (those should result in 'Failed' status with red background)
   */
  hasRowValidationError(control: FieldControl): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    return true;
  }

  /**
   * Check if row has cross-sheet validation error (for red highlighting).
   * True when the deferred cross-sheet equality pass (validateCrossSheet) flagged
   * this control — today the MPERS inventory write-down ↔ Role 18 SOCF tie-out.
   */
  hasCrossSheetValidationError(control: FieldControl): boolean {
    return this.equalityValidationErrors.has(control.qname);
  }

  /**
   * Check if a specific field (control + column suffix) has a mismatch error —
   * drives the per-input red border. Mirrors role 18: match the column label
   * embedded in the cross-sheet equality message, excluding suggested-change
   * ("does not match the extracted sum") warnings which are yellow, not red.
   */
  hasFieldMismatchError(control: FieldControl, suffix: string): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    const errors = this.validationErrors.get(control.qname) || [];

    // 'ConsolidatedCurrent' -> 'Consolidated Current', 'SeparatePrevious' -> 'Separate Previous'
    const columnLabel = suffix
      .replace('Consolidated', 'Consolidated ')
      .replace('Separate', 'Separate ');

    return errors.some(
      (error) => error.includes(columnLabel) && !error.includes('does not match the extracted sum')
    );
  }

  onCancelConfirmed(): void {
    // Set flag to skip validation during cancel flow
    this.isCancellingChanges = true;

    // Clear all editing states
    this.editingRows.clear();
    this.frozenRowStatus.clear();
    this.stateService.clearSheetEditing(SHEET_KEYS.analysisOfProfitOrLoss);

    // Clear validation errors
    this.validationErrors.clear();

    // Reload from the reverted state service data (original API values)
    this.isValidationComplete = false;
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.analysisOfProfitOrLossForm.markAsPristine();

    // Reset flag after loadSavedState's setTimeout completes (100ms + buffer)
    setTimeout(() => {
      this.isCancellingChanges = false;
    }, 150);
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FieldControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
